import {useState} from 'react';
import {ArrowRight, Loader2, Search} from 'lucide-react';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {trpc} from '@common-web/lib/trpc';
import {useDebounce} from '@common-web/hooks/use-debounce';
import {WizardState} from './types';
import {WIZARD_STEPS} from './utils';

export function StepAgentLookup({
  state,
  onUpdate,
  onNext,
}: {
  state: WizardState;
  onUpdate: (partial: Partial<WizardState>) => void;
  onNext: () => void;
}) {
  const [agentSearch, setAgentSearch] = useState('');
  const debouncedSearch = useDebounce(agentSearch, 400);

  const agentsQuery = trpc.vba.agents.listAgents.useQuery(
    {search: debouncedSearch, pageSize: 10},
    {enabled: debouncedSearch.length >= 2},
  );

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardContent className='p-6'>
        <h3 className='text-lg font-semibold text-brand-navy mb-1'>{WIZARD_STEPS[0].label}</h3>
        <p className='text-sm text-muted-foreground mb-6'>
          Search for a commission agent to associate with this group.
        </p>

        {state.selectedAgent ? (
          <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
            <div className='flex items-start justify-between'>
              <div className='space-y-1'>
                <p className='font-medium text-gray-900'>{state.selectedAgent.fullName}</p>
                {state.selectedAgent.admin123AgentId && (
                  <p className='text-sm text-gray-500'>
                    Agent ID: {state.selectedAgent.admin123AgentId}
                  </p>
                )}
                {state.selectedAgent.email && (
                  <p className='text-sm text-gray-500'>{state.selectedAgent.email}</p>
                )}
                {state.selectedAgent.phone && (
                  <p className='text-sm text-gray-500'>{state.selectedAgent.phone}</p>
                )}
              </div>
              <Button
                variant='link'
                size='sm'
                onClick={() => onUpdate({selectedAgent: null})}
                className='text-brand-blue h-auto p-0'
              >
                Change
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
              <Input
                placeholder='Search by name, email, or agent ID...'
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                className='pl-10'
              />
            </div>

            {agentsQuery.isLoading && (
              <div className='flex justify-center py-8'>
                <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
              </div>
            )}

            {agentsQuery.data && (
              <div className='mt-4 space-y-2'>
                {agentsQuery.data.agents.length === 0 ? (
                  <p className='text-sm text-gray-500 text-center py-4'>
                    No agents found matching &ldquo;{debouncedSearch}&rdquo;
                  </p>
                ) : (
                  agentsQuery.data.agents.map((agent) => (
                    <div
                      key={agent.id}
                      className='flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white cursor-pointer hover:border-brand-blue transition-colors'
                      onClick={() => onUpdate({selectedAgent: agent})}
                    >
                      <div>
                        <p className='font-medium text-gray-900'>{agent.fullName}</p>
                        <p className='text-sm text-gray-500'>
                          {agent.email}
                          {agent.admin123AgentId && ` · ${agent.admin123AgentId}`}
                        </p>
                      </div>
                      <Button variant='outline' size='sm'>
                        Select
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        <div className='flex justify-end mt-6 pt-4 border-t'>
          <Button onClick={onNext} disabled={!state.selectedAgent}>
            Continue
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
